import { useState, type ComponentType } from 'react';
import {
  Package, Plus, ExternalLink, Pencil, Trash2, Clock, Coins, Layers3,
} from 'lucide-react';
import { labApi } from '@/lib/api';
import { EmptyState } from '@/components/EmptyState';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import type { LabSupplier } from './types';
import { SUPPLIER_TYPE_LABELS } from './constants';
import { SupplierModal } from './SupplierModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';

interface SupplierSectionProps {
  productId: string;
  suppliers: LabSupplier[];
  onReload: () => void;
}

const TYPE_BADGE_COLORS: Record<string, BadgeVariant> = {
  MARKETPLACE: 'info',
  WHOLESALER: 'accent',
  PRODUCER: 'success',
  DROPSHIPPING: 'warning',
};

export function SupplierSection({ productId, suppliers, onReload }: SupplierSectionProps) {
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<LabSupplier | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleSave = async (data: Record<string, unknown>) => {
    if (editingSupplier) {
      await labApi.updateSupplier(productId, editingSupplier.id, data);
    } else {
      await labApi.createSupplier(productId, data);
    }
    setShowModal(false);
    setEditingSupplier(null);
    onReload();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await labApi.deleteSupplier(productId, deleteId);
    setDeleteId(null);
    onReload();
  };

  const suppliersByType = suppliers.reduce<Record<string, number>>((acc, supplier) => {
    acc[supplier.type] = (acc[supplier.type] || 0) + 1;
    return acc;
  }, {});
  const suppliersWithPrice = suppliers.filter(supplier => supplier.unitPrice?.trim()).length;
  const suppliersWithDelivery = suppliers.filter(supplier => supplier.deliveryTime?.trim()).length;

  return (
    <section className="space-y-5">
      <Card padding="md" className="bg-accent-primary/5 border-accent-primary/20">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-card bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center shrink-0">
              <Package className="w-6 h-6 text-accent-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Dostawcy</h2>
              <p className="text-sm text-text-muted mt-1 max-w-2xl">
                Lista potencjalnych źródeł zakupu, warunków dostawy i notatek operacyjnych dla produktu.
              </p>
            </div>
          </div>
          <Button
            onClick={() => { setEditingSupplier(null); setShowModal(true); }}
            leftIcon={<Plus />}
          >
            Dodaj dostawcę
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          <div className="rounded-card border border-border-subtle bg-surface-base/30 p-3">
            <span className="text-xs text-text-muted">Dostawcy</span>
            <strong className="block text-2xl font-semibold text-text-primary mt-1">{suppliers.length}</strong>
          </div>
          <div className="rounded-card border border-border-subtle bg-surface-base/30 p-3">
            <span className="text-xs text-text-muted">Z ceną</span>
            <strong className="block text-2xl font-semibold text-text-primary mt-1">{suppliersWithPrice}</strong>
          </div>
          <div className="rounded-card border border-border-subtle bg-surface-base/30 p-3">
            <span className="text-xs text-text-muted">Z dostawą</span>
            <strong className="block text-2xl font-semibold text-text-primary mt-1">{suppliersWithDelivery}</strong>
          </div>
          <div className="rounded-card border border-border-subtle bg-surface-base/30 p-3">
            <span className="text-xs text-text-muted">Typy źródeł</span>
            <strong className="block text-2xl font-semibold text-accent-primary mt-1">{Object.keys(suppliersByType).length}</strong>
          </div>
        </div>
      </Card>

      {suppliers.length === 0 ? (
        <EmptyState
          icon={Package}
          title="Brak dostawców"
          description="Dodaj pierwszego dostawcę dla tego produktu."
          buttonLabel="Dodaj dostawcę"
          onButtonClick={() => { setEditingSupplier(null); setShowModal(true); }}
          buttonIcon={Plus}
          size="sm"
          iconColorClass="from-accent-primary/20 to-accent-info/20 border-accent-primary/20 text-accent-primary"
        />
      ) : (
        <div className="space-y-3">
          {suppliers.map(s => (
            <Card
              key={s.id}
              padding="md"
              className="hover:border-accent-primary/30"
            >
              <div className="grid grid-cols-1 xl:grid-cols-[minmax(220px,1.2fr)_minmax(360px,1.4fr)_auto] gap-4 items-start">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-card bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center shrink-0">
                    <Package className="w-5 h-5 text-accent-primary" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold text-text-primary truncate">{s.name}</h4>
                    <Badge variant={TYPE_BADGE_COLORS[s.type] || 'neutral'} size="sm" className="mt-1">
                      {SUPPLIER_TYPE_LABELS[s.type] || s.type}
                    </Badge>
                    {s.url && (
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs text-accent-primary hover:text-accent-info transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Źródło
                      </a>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <SupplierMetric icon={Coins} label="Cena" value={s.unitPrice} />
                  <SupplierMetric icon={Layers3} label="MOQ" value={s.moq} />
                  <SupplierMetric icon={Clock} label="Dostawa" value={s.deliveryTime} />
                  {s.notes && (
                    <div className="md:col-span-3 rounded-card border border-border-subtle bg-surface-base/30 p-3">
                      <span className="text-xs text-text-muted">Notatki</span>
                      <p className="text-sm text-text-secondary mt-1 whitespace-pre-wrap">{s.notes}</p>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="icon"
                    size="sm"
                    onClick={() => { setEditingSupplier(s); setShowModal(true); }}
                    aria-label={`Edytuj dostawcę ${s.name}`}
                    leftIcon={<Pencil />}
                  />
                  <Button
                    variant="icon"
                    size="sm"
                    onClick={() => setDeleteId(s.id)}
                    aria-label={`Usuń dostawcę ${s.name}`}
                    leftIcon={<Trash2 />}
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showModal && (
        <SupplierModal
          supplier={editingSupplier}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingSupplier(null); }}
        />
      )}

      {deleteId && (
        <DeleteConfirmModal
          title="Usuń dostawcę"
          message="Czy na pewno chcesz usunąć tego dostawcę? Tej operacji nie można cofnąć."
          onConfirm={handleDelete}
          onClose={() => setDeleteId(null)}
        />
      )}
    </section>
  );
}

function SupplierMetric({
  icon: Icon,
  label,
  value,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value?: string | null;
}) {
  return (
    <div className="rounded-card border border-border-subtle bg-surface-base/30 p-3">
      <div className="flex items-center gap-1.5 text-xs text-text-muted">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <p className="text-sm font-medium text-text-primary mt-1 truncate">{value?.trim() || '-'}</p>
    </div>
  );
}
